import { Injectable } from '@angular/core';
import * as XLSX from 'xlsx';

const CDR_COLUMN_MAPPING: Record<string, string> = {
  'id':   'ID de registro',
  'time': 'Fecha',
  'from': 'Origen',
  'to':   'Destino',
};
const CDR_CHARGE_DETAILS_COLUMN = 'charge details';
const CDR_SEGMENTS_OUTPUT       = 'Segmentos';
const CDR_UNIT_PRICE_OUTPUT     = 'Precio segmento';
const CDR_PRICE_OUTPUT          = 'Precio';

export interface CdrProcessResult {
  rows: any[];
  stats: { valid: number; wrongType: number; emptyChargeDetails: number };
  totalSegments: number;
  totalPrice: number;
  log: string[];
}

@Injectable({ providedIn: 'root' })
export class CdrProcessingService {

  async processBlob(blob: Blob, pricePerSms: number, type = 'CPAAS_SMS'): Promise<CdrProcessResult> {
    const rows = await this.readSpreadsheet(blob);
    const log: string[] = [];
    log.push(`   Filas leídas: ${rows.length}`);

    const { transformed, stats } = this.filterAndTransformRows(rows, type, pricePerSms);

    log.push(`   ✓ Filas válidas: ${stats.valid}`);
    if (stats.wrongType > 0)
      log.push(`   ✗ Descartadas por tipo distinto a "${type}": ${stats.wrongType}`);
    if (stats.emptyChargeDetails > 0)
      log.push(`   ✗ Descartadas por "charge details" vacío: ${stats.emptyChargeDetails}`);

    const totalSegments = transformed.reduce((sum, row) => {
      const seg = row[CDR_SEGMENTS_OUTPUT];
      return sum + (typeof seg === 'number' ? seg : 0);
    }, 0);

    return { rows: transformed, stats, totalSegments, totalPrice: totalSegments * pricePerSms, log };
  }

  addTotalRow(rows: any[], pricePerSms: number): any[] {
    const totalSegments = rows.reduce((sum, row) => {
      const seg = row[CDR_SEGMENTS_OUTPUT];
      return sum + (typeof seg === 'number' ? seg : 0);
    }, 0);
    const totalPrice = totalSegments * pricePerSms;
    const totalRow: Record<string, any> = {
      'ID de registro':       'TOTAL',
      'Fecha':                '',
      'Origen':               '',
      'Destino':              '',
      [CDR_SEGMENTS_OUTPUT]:  totalSegments,
      [CDR_UNIT_PRICE_OUTPUT]: pricePerSms.toFixed(5),
      [CDR_PRICE_OUTPUT]:     totalPrice.toFixed(5),
    };
    return [...rows, totalRow];
  }

  generateXlsx(data: any[], filename: string): void {
    const worksheet  = XLSX.utils.json_to_sheet(data);
    const workbook   = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'CDR');
    const content    = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const blob       = new Blob([content], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const url        = window.URL.createObjectURL(blob);
    const a          = document.createElement('a');
    a.href           = url;
    a.download       = `${filename}.xlsx`;
    a.click();
    window.URL.revokeObjectURL(url);
  }

  // ── Private helpers ───────────────────────────────────────────────────────

  private filterAndTransformRows(rows: any[], type: string, pricePerSms: number) {
    const transformed: any[] = [];
    const stats = { valid: 0, wrongType: 0, emptyChargeDetails: 0 };

    for (const row of rows) {
      if (row['type'] !== type)              { stats.wrongType++;          continue; }
      if (!row[CDR_CHARGE_DETAILS_COLUMN])   { stats.emptyChargeDetails++; continue; }

      const out: Record<string, any> = {};
      for (const [src, dst] of Object.entries(CDR_COLUMN_MAPPING)) {
        out[dst] = row[src] ?? '';
      }
      const { unitPrice, price } = this.parseChargeDetails(row[CDR_CHARGE_DETAILS_COLUMN]);
      const segments   = this.calculateSegments(unitPrice, price);
      const totalPrice = typeof segments === 'number' ? segments * pricePerSms : 0;
      out[CDR_SEGMENTS_OUTPUT]   = segments;
      out[CDR_UNIT_PRICE_OUTPUT] = pricePerSms.toFixed(5);
      out[CDR_PRICE_OUTPUT]      = totalPrice.toFixed(5);

      transformed.push(out);
      stats.valid++;
    }
    return { transformed, stats };
  }

  private parseChargeDetails(s: string): { unitPrice: string; price: string } {
    return {
      unitPrice: s.match(/unitPrice=([0-9.]+)/)?.[1]    ?? '',
      price:     s.match(/,\s*price=([0-9.]+)/)?.[1]    ?? '',
    };
  }

  private calculateSegments(unitPrice: string, price: string): number | string {
    const u = parseFloat(unitPrice);
    const p = parseFloat(price);
    if (isNaN(u) || isNaN(p) || u === 0) return '';
    return Math.round(p / u);
  }

  private readSpreadsheet(blob: Blob): Promise<any[]> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e: any) => {
        try {
          const data      = new Uint8Array(e.target.result);
          const workbook  = XLSX.read(data, { type: 'array' });
          const worksheet = workbook.Sheets[workbook.SheetNames[0]];
          resolve(XLSX.utils.sheet_to_json(worksheet, { defval: '' }) as any[]);
        } catch (err) { reject(err); }
      };
      reader.onerror = reject;
      reader.readAsArrayBuffer(blob);
    });
  }
}
